import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { approveRateDrafts, approveRateDraftsOnActiveLooms, listRateDrafts } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { confirmDialog } from "../ui/dialog";
import { fmtDay } from "./rateModel";
import { activeLoomDesigns, ApproveResult, DraftDesign, noteTitle, otherNotes, sortResults } from "./rateDraftsModel";
import { kp } from "../karkhanaPaths";

/**
 * Rate drafts waiting for approval, by design. Drafts pay nothing; the owner approves them together
 * (every dated version of a design at once), or only the designs on looms now. Managers see the list.
 */
const RateDraftsSection = ({ owner }: { owner: boolean }) => {
  const { t } = useKT();
  const [designs, setDesigns] = useState<DraftDesign[] | null>(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const res: any = await listRateDrafts();
      if (res?.success) {
        setDesigns(res.data || []);
        setError("");
      } else {
        setError(res?.message || "Could not load");
      }
    } catch {
      setError("Could not reach the server");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const run = async (activeOnly: boolean) => {
    const list = activeOnly ? activeLoomDesigns(designs || [], picked) : (designs || []).filter((d) => picked.has(d.designId));
    if (!list.length) {
      notifications({ title: t("Nothing to approve"), message: t(activeOnly ? "None of these designs is on a loom now." : "Tick the designs to approve."), type: "warning" });
      return;
    }
    const drafts = list.reduce((n, d) => n + d.drafts.length, 0);
    if (!(await confirmDialog({ title: t("Approve {{n}} drafts for {{d}} designs? Approved rates pay work from their start dates.", { n: drafts, d: list.length }) }))) return;
    setBusy(true);
    try {
      // Active looms with nothing ticked: no list, so the server takes every design on a loom now.
      const res: any = activeOnly
        ? await approveRateDraftsOnActiveLooms(Array.from(picked))
        : await approveRateDrafts(list.map((d) => d.designId));
      if (!res?.success) {
        notifications({ title: t("Not approved"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      setResult(res.data);
      setPicked(new Set());
      notifications({
        title: t("Done"),
        message: t("{{a}} approved · {{n}} not approved", { a: res.data?.approved ?? 0, n: res.data?.notApproved ?? 0 }),
        type: res.data?.notApproved ? "warning" : "success",
      });
      load();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="kw-muted">{t(error)}</div>;
  if (!designs) return null;
  if (!designs.length && !result) return null;

  const results: ApproveResult[] = sortResults(result?.results || []);
  const notes = otherNotes(result);

  return (
    <section className="kr-drafts">
      <h3 className="kr-group-title">{t("Rate drafts")}</h3>
      <p className="kw-muted">
        {t("Drafts pay nothing until approved. A design can wait with several dated versions; they are approved together.")}
        {!owner ? ` ${t("The studio owner approves them.")}` : ""}
      </p>
      {designs.map((d) => (
        <label key={d.id} className={`kw-card kr-drafts__row${picked.has(d.designId) ? " is-picked" : ""}`}>
          {owner ? <input type="checkbox" checked={picked.has(d.designId)} onChange={() => toggle(d.designId)} aria-label={d.designNumber} /> : null}
          <span className="kr-drafts__main">
            <Link to={kp.rateCard(d.id)} className="kw-code" onClick={(e) => e.stopPropagation()}>
              {d.designNumber}
            </Link>
            {d.onActiveLoom ? <span className="kw-chip is-info">{t("on a loom now")}</span> : null}
            <small>
              {d.drafts.map((v) => `v${v.versionNo} ${t("from {{from}}", { from: fmtDay(v.effectiveFrom) })}`).join(" · ")}
            </small>
          </span>
        </label>
      ))}
      {owner && designs.length ? (
        <div className="kr-drafts__actions">
          <Button className="ResetBtn" disabled={busy || !picked.size} onClick={() => run(false)}>
            {picked.size ? t("Approve all drafts for these {{n}} designs", { n: picked.size }) : t("Approve all drafts for these designs")}
          </Button>
          <Button outline disabled={busy || !activeLoomDesigns(designs, picked).length} onClick={() => run(true)}>
            {t("Approve only designs on active looms")}
          </Button>
        </div>
      ) : null}

      {result ? (
        <div className="kw-card kr-drafts__result" aria-live="polite">
          <strong>{t("{{a}} approved · {{n}} not approved", { a: result.approved ?? 0, n: result.notApproved ?? 0 })}</strong>
          <ul className="kh-changes">
            {results.map((r, i) => (
              <li key={i}>
                <span className="kw-code">{r.designNumber}</span> v{r.versionNo} · {t("from {{from}}", { from: fmtDay(r.effectiveFrom) })}
                {r.effectiveTo ? ` ${t("to {{to}}", { to: fmtDay(r.effectiveTo) })}` : ""}{" "}
                <span className={`kw-chip${r.status === "APPROVED" ? " is-ok" : " is-review"}`}>{r.status === "APPROVED" ? t("Approved") : t("Not approved")}</span>
                {r.reason ? <small>{t(r.reason)}</small> : null}
              </li>
            ))}
          </ul>
          {notes.map((n) => (
            <div key={n.key} className="mt-2">
              <strong>{t(noteTitle(n.key))}</strong>
              <ul className="kh-changes">
                {n.lines.map((line, i) => (
                  <li key={i}>{t(line)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default RateDraftsSection;
